/**
 * Settings Module
 * Handles user preferences, profile management, and app configuration
 */

import { getCurrentUser } from './auth.js';
import { db } from './firebase.js';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';

/**
 * Settings State
 */
let settingsState = {
  currentSection: 'profile',
  userSettings: null,
  isDirty: false, // Tracks if settings have been modified
  autoSaveTimeout: null
};

/**
 * Default Settings Structure
 */
const defaultSettings = {
  // Profile Information
  displayName: '',
  phone: '',
  timezone: 'auto',
  currency: 'USD',
  dateFormat: 'MM/DD/YYYY',
  profilePictureUrl: '',
  
  // Notification Preferences
  notifications: {
    pushEnabled: true,
    emailEnabled: true,
    smsEnabled: false,
    quietHours: {
      enabled: false,
      startTime: '22:00',
      endTime: '08:00'
    },
    budgetAlerts: {
      enabled: true,
      thresholds: [80, 90, 100]
    },
    breakdownEnabled: true,
    breakdownMethod: 'email',
    breakdownFrequency: 'weekly',
    sharingAlerts: true,
    marketingOptIn: false
  },
  
  // App Preferences
  interface: {
    theme: 'system',
    accentColor: '#667eea',
    fontSize: 'medium',
    layoutDensity: 'spacious',
    defaultViewDesktop: 'budget',
    defaultViewMobile: 'expenses',
    highContrast: false,
    reducedMotion: false
  },
  
  // Privacy Settings
  privacy: {
    contactVisibility: 'collaborators',
    activityVisibility: 'collaborators',
    profileVisibility: 'collaborators',
    analyticsOptOut: false,
    marketingOptOut: false,
    crashReporting: true
  },
  
  // Sharing Preferences
  sharing: {
    defaultPermissionLevel: 'contribute',
    allowDirectInvites: true,
    requireApprovalForSharing: false,
    autoAcceptFromDomains: []
  },
  
  // App Behavior
  behavior: {
    confirmDelete: true,
    confirmLargeExpenses: true,
    largeExpenseThreshold: 100,
    autoSaveFrequency: 30, // seconds
    offlineMode: true
  },
  
  // Metadata
  createdAt: new Date(),
  updatedAt: new Date()
};

/**
 * Ensure correct DOM structure for settings
 * Fix issue where settings-mode gets nested inside other mode containers
 */
function ensureCorrectDOMStructure() {
  const settingsMode = document.getElementById('settings-mode');
  const content = document.querySelector('.content');
  
  if (!settingsMode || !content) return;
  
  // Check if settings-mode is properly positioned as direct child of content
  if (settingsMode.parentElement !== content) {
    console.log('Fixing settings DOM structure - moving to correct parent');
    
    // Remove from current position
    settingsMode.remove();
    
    // Add as direct child of content container
    content.appendChild(settingsMode);
    
    console.log('Settings DOM structure fixed');
  }
}

/**
 * Initialize Settings Module
 */
export async function initializeSettings() {
  console.log('Initializing settings module...');
  
  try {
    // Fix DOM structure - ensure settings-mode is not nested inside other mode containers
    ensureCorrectDOMStructure();
    // Set up event listeners
    setupSettingsNavigation();
    setupFormHandlers();
    
    // Household sharing replaced by link sharing - no initialization needed
    
    // Load user settings if authenticated
    const user = getCurrentUser();
    if (user) {
      await loadUserSettings(user.uid);
    }
    
    console.log('Settings module initialized successfully');
  } catch (error) {
    console.error('Failed to initialize settings:', error);
  }
}

/**
 * Setup Settings Navigation
 */
function setupSettingsNavigation() {
  const navItems = document.querySelectorAll('.settings-nav-item');
  const sections = document.querySelectorAll('.settings-section');
  
  navItems.forEach(item => {
    item.addEventListener('click', (e) => {
      e.preventDefault();
      const sectionId = item.dataset.section;
      switchToSection(sectionId);
    });
  });
}

/**
 * Switch to Settings Section
 */
function switchToSection(sectionId) {
  // Update navigation
  document.querySelectorAll('.settings-nav-item').forEach(item => {
    item.classList.remove('active');
  });
  document.querySelector(`[data-section="${sectionId}"]`).classList.add('active');
  
  // Update content
  document.querySelectorAll('.settings-section').forEach(section => {
    section.classList.remove('active');
  });
  document.getElementById(`${sectionId}-section`).classList.add('active');
  
  settingsState.currentSection = sectionId;
}

/**
 * Setup Form Handlers
 */
function setupFormHandlers() {
  // Get all form inputs
  const inputs = document.querySelectorAll('.settings-content input, .settings-content select');
  
  inputs.forEach(input => {
    input.addEventListener('change', handleSettingChange);
    input.addEventListener('input', handleSettingChange);
  });
  
  // Setup specific toggle switch handlers
  setupToggleSwitches();
  
  // Setup button handlers
  setupButtonHandlers();
}

/**
 * Setup Toggle Switch Handlers
 */
function setupToggleSwitches() {
  const toggleContainers = document.querySelectorAll('.toggle-switch');
  
  toggleContainers.forEach(container => {
    const checkbox = container.querySelector('input[type="checkbox"]');
    const slider = container.querySelector('.toggle-slider');
    
    if (checkbox && slider) {
      // Handle clicks on the slider (visual element)
      slider.addEventListener('click', (e) => {
        e.preventDefault();
        checkbox.checked = !checkbox.checked;
        console.log(`Toggle clicked: ${checkbox.id} = ${checkbox.checked}`);
        
        // Trigger change event manually
        const changeEvent = new Event('change', { bubbles: true });
        checkbox.dispatchEvent(changeEvent);
      });
      
      // Handle change events on the actual checkbox
      checkbox.addEventListener('change', (e) => {
        console.log(`Toggle changed: ${e.target.id} = ${e.target.checked}`);
        handleSettingChange(e);
      });
    }
  });
}

/**
 * Handle Setting Change
 */
function handleSettingChange(e) {
  const { target } = e;
  const { id, type, value, checked } = target;
  
  if (!settingsState.userSettings) return;
  
  // Update the settings object based on the input
  updateSettingValue(id, type === 'checkbox' ? checked : value);
  
  // Mark as dirty and schedule auto-save
  settingsState.isDirty = true;
  scheduleAutoSave();
  
  // Update UI to show unsaved changes
  updateSaveStatus('unsaved');
}

/**
 * Update Setting Value in State
 */
function updateSettingValue(settingId, value) {
  const settings = settingsState.userSettings;
  
  // Map setting IDs to settings object paths
  const settingMappings = {
    'display-name': () => settings.displayName = value,
    'phone-number': () => settings.phone = value,
    'currency-preference': () => settings.currency = value,
    'timezone': () => settings.timezone = value,
    'breakdown-enabled': () => settings.notifications.breakdownEnabled = value,
    'breakdown-method': () => settings.notifications.breakdownMethod = value,
    'breakdown-frequency': () => settings.notifications.breakdownFrequency = value,
    'budget-alerts-enabled': () => settings.notifications.budgetAlerts.enabled = value,
    'threshold-80': () => updateThreshold(80, value),
    'threshold-90': () => updateThreshold(90, value),
    'threshold-100': () => updateThreshold(100, value),
    'quiet-hours-enabled': () => settings.notifications.quietHours.enabled = value,
    'quiet-start': () => settings.notifications.quietHours.startTime = value,
    'quiet-end': () => settings.notifications.quietHours.endTime = value,
    'theme-light': () => value && (settings.interface.theme = 'light'),
    'theme-dark': () => value && (settings.interface.theme = 'dark'),
    'theme-system': () => value && (settings.interface.theme = 'system'),
    'font-size': () => settings.interface.fontSize = value,
    'layout-density': () => settings.interface.layoutDensity = value,
    'high-contrast': () => settings.interface.highContrast = value,
    'reduced-motion': () => settings.interface.reducedMotion = value,
    'default-permission': () => settings.sharing.defaultPermissionLevel = value,
    'require-approval': () => settings.sharing.requireApprovalForSharing = value,
    'contact-visibility': () => settings.privacy.contactVisibility = value,
    'activity-visibility': () => settings.privacy.activityVisibility = value,
    'analytics-opt-out': () => settings.privacy.analyticsOptOut = value,
    'marketing-opt-out': () => settings.privacy.marketingOptOut = value,
    'crash-reporting': () => settings.privacy.crashReporting = value,
    'default-view-desktop': () => settings.interface.defaultViewDesktop = value,
    'default-view-mobile': () => settings.interface.defaultViewMobile = value,
    'confirm-delete': () => settings.behavior.confirmDelete = value,
    'confirm-large-expenses': () => settings.behavior.confirmLargeExpenses = value,
    'auto-save': () => settings.behavior.autoSaveFrequency = parseInt(value) || 30,
    'offline-mode': () => settings.behavior.offlineMode = value
  };
  
  const updateFunction = settingMappings[settingId];
  if (updateFunction) {
    updateFunction();
    settings.updatedAt = new Date();
  }
}

/**
 * Update Budget Alert Threshold
 */
function updateThreshold(threshold, enabled) {
  const settings = settingsState.userSettings;
  const thresholds = settings.notifications.budgetAlerts.thresholds;
  
  if (enabled && !thresholds.includes(threshold)) {
    thresholds.push(threshold);
    thresholds.sort((a, b) => a - b);
  } else if (!enabled && thresholds.includes(threshold)) {
    const index = thresholds.indexOf(threshold);
    thresholds.splice(index, 1);
  }
}

/**
 * Setup Button Handlers
 */
function setupButtonHandlers() {
  // Save Settings Button
  const saveBtn = document.getElementById('save-settings');
  if (saveBtn) {
    saveBtn.addEventListener('click', saveSettings);
  }
  
  // Reset Settings Button
  const resetBtn = document.getElementById('reset-settings');
  if (resetBtn) {
    resetBtn.addEventListener('click', resetSettings);
  }
  
  // Export buttons
  setupExportButtons();
  
  // Other action buttons
  setupActionButtons();
}

/**
 * Setup Export Buttons
 */
function setupExportButtons() {
  // These would integrate with the actual export functionality
  const exportButtons = document.querySelectorAll('.export-options .settings-btn');
  exportButtons.forEach(btn => {
    btn.addEventListener('click', (e) => {
      const text = e.target.textContent;
      if (text.includes('JSON')) {
        exportUserData('json');
      } else if (text.includes('PDF')) {
        exportUserData('pdf');
      } else if (text.includes('CSV')) {
        exportUserData('csv');
      }
    });
  });
}

/**
 * Setup Action Buttons
 */
function setupActionButtons() {
  // Helper function to find button by text content
  function findButtonByText(text) {
    const buttons = document.querySelectorAll('.settings-content button');
    return Array.from(buttons).find(button => 
      button.textContent.trim().includes(text)
    );
  }
  
  // Change Password
  const changePasswordBtn = findButtonByText('Change Password');
  if (changePasswordBtn) {
    changePasswordBtn.addEventListener('click', handleChangePassword);
  }
  
  // Data Download
  const downloadDataBtn = findButtonByText('Download My Data');
  if (downloadDataBtn) {
    downloadDataBtn.addEventListener('click', handleDownloadData);
  }
  
  // Manage Household Sharing (now handled in hamburger menu)
  // const manageHouseholdBtn = document.getElementById('manage-household-btn');
  // if (manageHouseholdBtn) {
  //   manageHouseholdBtn.addEventListener('click', handleManageSharedBudgets);
  // }
  
  // Delete Account
  const deleteAccountBtn = findButtonByText('Delete My Account');
  if (deleteAccountBtn) {
    deleteAccountBtn.addEventListener('click', handleDeleteAccount);
  }
}


/**
 * Load User Settings
 */
export async function loadUserSettings(userId) {
  try {
    updateSaveStatus('loading');
    
    const userDoc = await getDoc(doc(db, 'users', userId));
    
    if (userDoc.exists() && userDoc.data().settings) {
      settingsState.userSettings = { ...defaultSettings, ...userDoc.data().settings };
    } else {
      // Create default settings for new user
      settingsState.userSettings = { ...defaultSettings };
      await setDoc(doc(db, 'users', userId), { 
        settings: settingsState.userSettings 
      }, { merge: true });
    }
    
    // Populate the form with loaded settings
    populateSettingsForm();
    updateSaveStatus('saved');
    
  } catch (error) {
    console.error('Error loading user settings:', error);
    updateSaveStatus('error', 'Failed to load settings');
    
    // Use default settings as fallback
    settingsState.userSettings = { ...defaultSettings };
    populateSettingsForm();
  }
}

/**
 * Populate Settings Form
 */
function populateSettingsForm() {
  if (!settingsState.userSettings) return;
  
  const settings = settingsState.userSettings;
  
  // Basic Information
  setInputValue('display-name', settings.displayName);
  setInputValue('phone-number', settings.phone);
  setInputValue('currency-preference', settings.currency);
  setInputValue('timezone', settings.timezone);
  
  // Notifications
  setInputValue('breakdown-enabled', settings.notifications.breakdownEnabled);
  setInputValue('breakdown-method', settings.notifications.breakdownMethod);
  setInputValue('breakdown-frequency', settings.notifications.breakdownFrequency);
  setInputValue('budget-alerts-enabled', settings.notifications.budgetAlerts.enabled);
  
  // Threshold checkboxes
  setInputValue('threshold-80', settings.notifications.budgetAlerts.thresholds.includes(80));
  setInputValue('threshold-90', settings.notifications.budgetAlerts.thresholds.includes(90));
  setInputValue('threshold-100', settings.notifications.budgetAlerts.thresholds.includes(100));
  
  // Quiet hours
  setInputValue('quiet-hours-enabled', settings.notifications.quietHours.enabled);
  setInputValue('quiet-start', settings.notifications.quietHours.startTime);
  setInputValue('quiet-end', settings.notifications.quietHours.endTime);
  
  // Theme
  const themeRadio = document.querySelector(`input[name="theme"][value="${settings.interface.theme}"]`);
  if (themeRadio) themeRadio.checked = true;
  
  // Interface
  setInputValue('font-size', settings.interface.fontSize);
  setInputValue('layout-density', settings.interface.layoutDensity);
  setInputValue('high-contrast', settings.interface.highContrast);
  setInputValue('reduced-motion', settings.interface.reducedMotion);
  
  // Sharing
  setInputValue('default-permission', settings.sharing.defaultPermissionLevel);
  setInputValue('require-approval', settings.sharing.requireApprovalForSharing);
  
  // Privacy
  setInputValue('contact-visibility', settings.privacy.contactVisibility);
  setInputValue('activity-visibility', settings.privacy.activityVisibility);
  setInputValue('analytics-opt-out', settings.privacy.analyticsOptOut);
  setInputValue('marketing-opt-out', settings.privacy.marketingOptOut);
  setInputValue('crash-reporting', settings.privacy.crashReporting);
  
  // Behavior
  setInputValue('default-view-desktop', settings.interface.defaultViewDesktop);
  setInputValue('default-view-mobile', settings.interface.defaultViewMobile);
  setInputValue('confirm-delete', settings.behavior.confirmDelete);
  setInputValue('confirm-large-expenses', settings.behavior.confirmLargeExpenses);
  setInputValue('auto-save', settings.behavior.autoSaveFrequency.toString());
  setInputValue('offline-mode', settings.behavior.offlineMode);
  
  // Set email address (readonly from auth)
  const user = getCurrentUser();
  if (user) {
    setInputValue('email-address', user.email);
  }
}

/**
 * Helper: Set Input Value
 */
function setInputValue(id, value) {
  const element = document.getElementById(id);
  if (!element) return;
  
  if (element.type === 'checkbox') {
    element.checked = value;
  } else {
    element.value = value || '';
  }
}

/**
 * Schedule Auto-Save
 */
function scheduleAutoSave() {
  if (settingsState.autoSaveTimeout) {
    clearTimeout(settingsState.autoSaveTimeout);
  }
  
  const frequency = settingsState.userSettings?.behavior?.autoSaveFrequency || 30;
  if (frequency === 'manual') return;
  
  settingsState.autoSaveTimeout = setTimeout(() => {
    if (settingsState.isDirty) {
      saveSettings(true); // Auto-save
    }
  }, (frequency * 1000));
}

/**
 * Save Settings
 */
export async function saveSettings(isAutoSave = false) {
  if (!settingsState.userSettings || !settingsState.isDirty) return;
  
  const user = getCurrentUser();
  if (!user) {
    updateSaveStatus('error', 'Not authenticated');
    return;
  }
  
  try {
    updateSaveStatus('saving');
    
    settingsState.userSettings.updatedAt = new Date();
    
    await setDoc(doc(db, 'users', user.uid), { 
      settings: settingsState.userSettings 
    }, { merge: true });
    
    settingsState.isDirty = false;
    updateSaveStatus('saved', isAutoSave ? 'Auto-saved' : 'Settings saved');
    
    // Apply certain settings immediately
    applyImmediateSettings();
    
  } catch (error) {
    console.error('Error saving settings:', error);
    updateSaveStatus('error', 'Failed to save settings');
  }
}

/**
 * Reset Settings to Defaults
 */
function resetSettings() {
  if (!confirm('Are you sure you want to reset all settings to defaults? This cannot be undone.')) {
    return;
  }
  
  settingsState.userSettings = { ...defaultSettings };
  populateSettingsForm();
  settingsState.isDirty = true;
  updateSaveStatus('unsaved');
}

/**
 * Apply Immediate Settings
 */
function applyImmediateSettings() {
  const settings = settingsState.userSettings;
  if (!settings) return;
  
  // Apply theme
  applyTheme(settings.interface.theme);
  
  // Apply font size
  applyFontSize(settings.interface.fontSize);
  
  // Apply reduced motion
  if (settings.interface.reducedMotion) {
    document.body.classList.add('reduced-motion');
  } else {
    document.body.classList.remove('reduced-motion');
  }
  
  // Apply high contrast
  if (settings.interface.highContrast) {
    document.body.classList.add('high-contrast');
  } else {
    document.body.classList.remove('high-contrast');
  }
}

/**
 * Apply Theme
 */
function applyTheme(theme) {
  document.body.className = document.body.className.replace(/theme-\w+/g, '');
  
  if (theme === 'system') {
    // Use system preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    document.body.classList.add(prefersDark ? 'theme-dark' : 'theme-light');
  } else {
    document.body.classList.add(`theme-${theme}`);
  }
}

/**
 * Apply Font Size
 */
function applyFontSize(fontSize) {
  document.body.className = document.body.className.replace(/font-size-\w+/g, '');
  document.body.classList.add(`font-size-${fontSize}`);
}

/**
 * Update Save Status
 */
function updateSaveStatus(status, message = '') {
  const statusEl = document.getElementById('settings-status');
  if (!statusEl) return;
  
  statusEl.className = `settings-status ${status}`;
  
  switch (status) {
    case 'loading':
      statusEl.textContent = 'Loading settings...';
      break;
    case 'saving':
      statusEl.textContent = 'Saving...';
      break;
    case 'saved':
      statusEl.textContent = message || 'All changes saved';
      break;
    case 'unsaved':
      statusEl.textContent = 'You have unsaved changes';
      break;
    case 'error':
      statusEl.textContent = message || 'Error occurred';
      break;
    default:
      statusEl.textContent = message;
  }
}

/**
 * Export User Data
 */
function exportUserData(format) {
  // This would implement the actual export functionality
  console.log(`Exporting user data in ${format} format`);
  updateSaveStatus('info', `Preparing ${format.toUpperCase()} export...`);
  
  // Placeholder implementation
  setTimeout(() => {
    updateSaveStatus('saved', `${format.toUpperCase()} export ready for download`);
  }, 2000);
}

/**
 * Action Button Handlers
 */
function handleChangePassword() {
  console.log('Change password clicked - not implemented yet');
  updateSaveStatus('info', 'Password change functionality coming soon');
}

function handleDownloadData() {
  console.log('Download data clicked - not implemented yet');
  updateSaveStatus('info', 'Data download functionality coming soon');
}

function handleManageSharedBudgets() {
  // Import and show household sharing modal
  import('./householdSharing.js').then(module => {
    module.showHouseholdSharingModal();
  }).catch(error => {
    console.error('Error loading household sharing:', error);
    updateSaveStatus('error', 'Failed to load household sharing');
  });
}

function handleDeleteAccount() {
  console.log('Delete account clicked - not implemented yet');
  if (confirm('Account deletion is not yet implemented. This feature will be available in a future update.')) {
    updateSaveStatus('info', 'Account deletion functionality coming soon');
  }
}

/**
 * Switch to Settings View
 */
export function switchToSettings() {
  // Hide all other views
  document.querySelectorAll('.single-mode, .compare-mode, .sankey-mode, .progress-view-mode, .recurring-calendar-mode, .expense-tracker-mode').forEach(mode => {
    mode.style.display = 'none';
  });
  
  // Show settings view
  const settingsMode = document.getElementById('settings-mode');
  if (settingsMode) {
    settingsMode.style.display = 'block';
  }
  
  // Update navigation
  document.querySelectorAll('.header-btn').forEach(btn => {
    btn.classList.remove('active');
  });
  document.getElementById('settings-btn')?.classList.add('active');
  
  // Load settings if not already loaded
  const user = getCurrentUser();
  if (user && !settingsState.userSettings) {
    loadUserSettings(user.uid);
  }
}

/**
 * Get Current Settings
 */
export function getCurrentSettings() {
  return settingsState.userSettings;
}

/**
 * Get Setting Value
 */
export function getSetting(path) {
  if (!settingsState.userSettings) return null;
  
  return path.split('.').reduce((obj, key) => obj?.[key], settingsState.userSettings);
}

/**
 * Update Setting Value
 */
export function updateSetting(path, value) {
  if (!settingsState.userSettings) return;
  
  const keys = path.split('.');
  const lastKey = keys.pop();
  const target = keys.reduce((obj, key) => obj[key], settingsState.userSettings);
  
  if (target) {
    target[lastKey] = value;
    settingsState.userSettings.updatedAt = new Date();
    settingsState.isDirty = true;
    scheduleAutoSave();
  }
}

/**
 * Get User's Preferred Currency
 */
export function getUserCurrency() {
  return getSetting('currency') || 'USD';
}

/**
 * Get User's Preferred Theme
 */
export function getUserTheme() {
  return getSetting('interface.theme') || 'system';
}

/**
 * Check if Analytics is Opted Out
 */
export function isAnalyticsOptedOut() {
  return getSetting('privacy.analyticsOptOut') || false;
}

/**
 * Check if Marketing is Opted Out
 */
export function isMarketingOptedOut() {
  return getSetting('privacy.marketingOptOut') || false;
}

/**
 * Get User's Preferred Default View Based on Device
 */
export function getDefaultView() {
  const isMobile = window.innerWidth <= 768; // Match CSS mobile breakpoint
  return isMobile ? 
    getSetting('interface.defaultViewMobile') || 'expenses' : 
    getSetting('interface.defaultViewDesktop') || 'budget';
}

/**
 * Check if Current Device is Mobile
 */
export function isMobileDevice() {
  return window.innerWidth <= 768;
}